import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Plus, Trophy, Trash2, ChevronRight, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TournamentsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [teamsText, setTeamsText] = useState('');
  const [creating, setCreating] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchTournaments();
  }, [token]);

  const fetchTournaments = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tournaments`, { headers });
      if (res.ok) setTournaments(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createTournament = async () => {
    const teams = teamsText.split('\n').map(t => t.trim()).filter(t => t);
    if (!name.trim() || teams.length < 2) return;
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tournaments`, { method: 'POST', headers, body: JSON.stringify({ name: name.trim(), teams }) });
      if (res.ok) { setName(''); setTeamsText(''); setShowCreate(false); fetchTournaments(); }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const deleteTournament = async (id) => {
    if (!window.confirm(t('confirmDelete', language))) return;
    await fetch(`${BACKEND_URL}/api/tournaments/${id}`, { method: 'DELETE', headers });
    fetchTournaments();
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{t('tournaments', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <div className="dashboard-intro">
          <h2 className="dashboard-title">{t('tournamentsTitle', language)}</h2>
          <p className="dashboard-desc">{t('tournamentsDesc', language)}</p>
        </div>

        <button className="class-create-btn" onClick={() => setShowCreate(true)} data-testid="create-tournament-btn">
          <Plus size={20} /> {t('createTournament', language)}
        </button>

        {showCreate && (
          <motion.div className="class-create-form" style={{ maxWidth: '500px' }} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <input className="setup-input setup-input-team1" type="text" value={name}
              onChange={e => setName(e.target.value)} placeholder={t('tournamentNamePlaceholder', language)} maxLength={40} />
            <label style={{ fontSize: '13px', fontWeight: 800, color: '#666' }}>{t('enterTeamsOnePerLine', language)}</label>
            <textarea className="setup-input" style={{ minHeight: '120px', resize: 'vertical', fontFamily: 'Nunito' }}
              value={teamsText} onChange={e => setTeamsText(e.target.value)}
              placeholder={'3 Bestari\n3 Cemerlang\n4 Bestari\n4 Cemerlang\n5 Bestari\n5 Cemerlang\n6 Bestari\n6 Cemerlang'} />
            <p style={{ fontSize: '12px', color: '#999' }}>{teamsText.split('\n').filter(t => t.trim()).length} {t('teamsAdded', language)}</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="auth-submit" style={{ flex: 1 }} onClick={createTournament} disabled={creating}>
                {creating ? '...' : t('create', language)}
              </button>
              <button className="upgrade-btn-secondary" onClick={() => setShowCreate(false)}>{t('cancel', language)}</button>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="lb-loading"><div className="lb-spinner" /></div>
        ) : tournaments.length === 0 ? (
          <div className="lb-empty">{t('noTournaments', language)}</div>
        ) : (
          <div className="classes-list">
            {tournaments.map((tn) => (
              <div key={tn._id} className="class-card">
                <div className="class-card-main" onClick={() => navigate(`/tournaments/${tn._id}`)}>
                  <div className="class-card-icon" style={{ background: tn.status === 'completed' ? '#fef3c7' : undefined, color: tn.status === 'completed' ? '#d97706' : undefined }}>
                    {tn.status === 'completed' ? <Crown size={22} /> : <Trophy size={22} />}
                  </div>
                  <div className="class-card-info">
                    <h3 className="class-card-name">{tn.name}</h3>
                    <p className="class-card-count">
                      {(tn.teams || []).length} {t('teamsLabel', language)}
                      {tn.status === 'completed' && tn.champion && ` • ${t('champion', language)}: ${tn.champion}`}
                    </p>
                  </div>
                  <span className={`tournament-status tournament-status-${tn.status}`}>{tn.status === 'completed' ? t('completed', language) : t('active', language)}</span>
                  <ChevronRight size={20} className="class-card-arrow" />
                </div>
                <button className="class-delete-btn" onClick={() => deleteTournament(tn._id)}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}

        <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>&larr; {t('backToHome', language)}</button>
      </div>
    </div>
  );
}
